import { useState } from "react"
import { Plus, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
const INITIAL_VALUES = {
  address: "",
  sourceLink: "",
  isHotLead: false,
}

export function AddDealDialog({ isOpen, onOpenChange, onAddDeal }) {
  const [values, setValues] = useState(INITIAL_VALUES)
  const [error, setError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const updateValue = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }))
    if (error) setError("")
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!values.address.trim()) {
      setError("Property address is required")
      return
    }
    setIsSubmitting(true)
    try {
      await onAddDeal(values)
      setValues(INITIAL_VALUES)
      setError("")
      onOpenChange(false)
    } catch (submitError) {
      setError(submitError.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-background/80 backdrop-blur-sm"
        className="max-w-md gap-0 overflow-hidden rounded-2xl bg-card p-0 shadow-xl sm:max-w-md"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between border-b border-border bg-muted/20 p-4">
          <DialogTitle className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
            <Plus className="h-4 w-4 text-primary" />
            Add Deal
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              className="p-1 text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Close Add Deal"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogClose>
        </div>
        <DialogDescription className="sr-only">
          Add a property to the acquisition pipeline for location and evidence analysis.
        </DialogDescription>

        <form className="space-y-4 p-6" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label
              htmlFor="deal-address"
              className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
            >
              Property Address
            </Label>
            <Input
              id="deal-address"
              value={values.address}
              placeholder="e.g. 12 Chester Road, Chester, CH1 2AB"
              className="rounded-lg bg-background px-3 py-2 text-sm font-medium placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary"
              aria-invalid={error === "Property address is required"}
              aria-describedby={error === "Property address is required" ? "deal-address-error" : undefined}
              onChange={(event) => updateValue("address", event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Include the full postcode where available so Sentinel can resolve the location and collect local evidence.
            </p>
            {error === "Property address is required" ? (
              <p id="deal-address-error" className="text-sm font-medium text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="deal-source-link"
              className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70"
            >
              Source Link (Optional)
            </Label>
            <Input
              id="deal-source-link"
              value={values.sourceLink}
              type="url"
              placeholder="Paste listing link (Rightmove, Zoopla, etc.)"
              className="rounded-lg bg-background px-3 py-2 text-sm font-medium placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary"
              onChange={(event) => updateValue("sourceLink", event.target.value)}
            />
          </div>

          <div className="flex items-center justify-between py-1">
            <div className="space-y-0.5">
              <Label
                id="deal-hot-lead-label"
                htmlFor="deal-hot-lead"
                className="text-sm font-bold text-foreground"
              >
                Mark as Hot Lead
              </Label>
              <p
                id="deal-hot-lead-description"
                className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/80"
              >
                Prioritise this deal for immediate attention
              </p>
            </div>
            <Switch
              id="deal-hot-lead"
              size="figma"
              checked={values.isHotLead}
              onCheckedChange={(checked) => updateValue("isHotLead", Boolean(checked))}
              aria-labelledby="deal-hot-lead-label"
              aria-describedby="deal-hot-lead-description"
              className="h-5 w-9 cursor-pointer justify-center shadow-none data-[state=unchecked]:bg-muted"
              thumbClassName="size-3.5 data-[state=checked]:translate-x-1.5 data-[state=unchecked]:-translate-x-1.5"
            />
          </div>

          {error && error !== "Property address is required" ? (
            <p className="text-sm font-medium text-destructive">
              {error}
            </p>
          ) : null}

          <div className="pt-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-auto w-full rounded-lg py-3 text-xs font-bold uppercase tracking-widest shadow-sm"
            >
              {isSubmitting ? "Adding Deal..." : "Add Deal to Pipeline"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
